import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { canonicalSegmentPrimaryLabel, SEGMENT_PRIMARY_VALUES } from "../constants";

const PRIORITY_VALUES = ["1", "2", "3", "4", "5"] as const;
const RATING_VALUES = ["", "high", "med"] as const;

function segmentOptionLabel(value: string): string {
  return canonicalSegmentPrimaryLabel(value).replaceAll("_", " ");
}

export function ChoicePill({
  value,
  options,
  open,
  onOpen,
  onClose,
  onSelect,
  formatLabel,
  emptyLabel = "—",
  ariaName,
  className,
  disabled = false,
  menuZIndex = 1700,
  menuAnchor = "auto",
}: {
  value: string;
  options: readonly string[];
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelect: (next: string) => void;
  formatLabel?: (value: string) => string;
  emptyLabel?: string;
  ariaName: string;
  className?: string;
  disabled?: boolean;
  menuZIndex?: number;
  menuAnchor?: "auto" | "above";
}) {
  const pillRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const labelOf = formatLabel ?? ((next: string) => next);
  const known = options.includes(value);
  const menuOptions = value && !known ? [value, ...options] : [...options];
  const label = value ? labelOf(value) : emptyLabel;

  useLayoutEffect(() => {
    if (!open || !pillRef.current) return;
    const rect = pillRef.current.getBoundingClientRect();
    const width = menuAnchor === "above" ? 280 : 260;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    const margin = 16;
    let top: number;
    let maxHeight: number;
    if (menuAnchor === "above") {
      const bottom = Math.max(margin + 160, rect.top - 8);
      top = margin;
      maxHeight = bottom - top;
    } else {
      maxHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      top = spaceBelow < 132 ? Math.max(margin, rect.top - maxHeight - 4) : rect.bottom + 4;
    }
    setMenuStyle({ top, left, width, maxHeight, zIndex: menuZIndex });
  }, [open, value, menuZIndex, menuAnchor]);

  useEffect(() => {
    if (!open) return;
    const insideMenu = (node: EventTarget | null) => {
      if (!(node instanceof Node) || !menuRef.current) return false;
      return node === menuRef.current || menuRef.current.contains(node);
    };
    const onPointer = (event: MouseEvent) => {
      const node = event.target;
      if (node instanceof Node && pillRef.current?.contains(node)) return;
      if (insideMenu(node)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    const onScroll = (event: Event) => {
      if (insideMenu(event.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [open, onClose]);

  const classNames = [
    "segment-pill",
    value ? null : "is-empty",
    open ? "is-open" : null,
    className || null,
  ]
    .filter(Boolean)
    .join(" ");

  if (disabled) {
    return (
      <span className={classNames} title={ariaName}>
        {label}
      </span>
    );
  }

  return (
    <div className="segment-pill-wrap">
      <button
        ref={pillRef}
        type="button"
        className={classNames}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={value ? `${ariaName} ${label}` : `Set ${ariaName}`}
        title={label}
        onClick={() => (open ? onClose() : onOpen())}
      >
        {label}
      </button>
      {open
        ? createPortal(
            <div ref={menuRef} className="segment-pill-menu" role="listbox" aria-label={ariaName} style={menuStyle}>
              {menuOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  role="option"
                  aria-selected={option === value}
                  className="segment-pill-option"
                  onClick={() => {
                    onClose();
                    if (option !== value) onSelect(option);
                  }}
                >
                  {labelOf(option)}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export function SegmentPill({
  value,
  open,
  onOpen,
  onClose,
  onSelect,
  disabled,
  menuZIndex,
  menuAnchor,
}: {
  value: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelect: (next: string) => void;
  disabled?: boolean;
  menuZIndex?: number;
  menuAnchor?: "auto" | "above";
}) {
  const canonical = value ? canonicalSegmentPrimaryLabel(value) : "";
  return (
    <ChoicePill
      value={canonical}
      options={SEGMENT_PRIMARY_VALUES}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      onSelect={onSelect}
      formatLabel={segmentOptionLabel}
      ariaName="Segment"
      disabled={disabled}
      menuZIndex={menuZIndex}
      menuAnchor={menuAnchor}
    />
  );
}

export function PriorityPill({
  value,
  open,
  onOpen,
  onClose,
  onSelect,
  disabled,
}: {
  value: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelect: (next: string) => void;
  disabled?: boolean;
}) {
  return (
    <ChoicePill
      value={value}
      options={PRIORITY_VALUES}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      onSelect={onSelect}
      formatLabel={(next) => `Pri ${next}`}
      emptyLabel="Pri —"
      ariaName="Priority"
      className={Number(value) >= 4 ? "is-warn" : undefined}
      disabled={disabled}
    />
  );
}

function ratingOptionLabel(value: string): string {
  if (value === "high") return "High";
  if (value === "med") return "Med";
  return "—";
}

export function RatingPill({
  value,
  open,
  onOpen,
  onClose,
  onSelect,
  disabled,
}: {
  value: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelect: (next: string) => void;
  disabled?: boolean;
}) {
  return (
    <ChoicePill
      value={value}
      options={RATING_VALUES}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      onSelect={onSelect}
      formatLabel={ratingOptionLabel}
      ariaName="My rating"
      disabled={disabled}
    />
  );
}
